/**
 * 管理员操作相关Hooks
 * 包含管理员的分配、移除、选择等功能
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, message, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTableSelectModal } from "@ms/shared-components";
import { Admin, adminSelectModalConfig } from "../config/modalAdminConfig";
import { roleApi } from "../utils/roleApi";

// ==================== 类型定义 ====================

export interface UseAdminSelectModalConfig {
  roleId: string;
  visible: boolean;
  onSuccess?: (selectedAdmins: Admin[]) => void;
}

export interface UseAdminSelectModalReturn {
  // 表格选择相关
  visible: boolean;
  loading: boolean;
  availableAdmins: Admin[];
  selectedAdmins: Admin[];
  selectedKeys: React.Key[];
  config: any; // 兼容 TableSelectModalConfig，但实际使用 GenericModalTable 的配置

  // 方法
  openModal: (initialConfig?: any) => void;
  closeModal: () => void;
  loadAvailableAdmins: () => Promise<void>;
  handleSelectionChange: (selectedKeys: React.Key[], selectedRows: Admin[]) => void;
  handleConfirm: (onConfirm?: (selectedKeys: React.Key[], selectedRows: Admin[]) => void) => void;
  handleCancel: () => void;
  handleClose: () => void;
  assignAdminsToRole: (adminIds: string[]) => Promise<void>;
}

export interface UseAssignAdminModalProps {
  visible: boolean;
  roleId: string;
  onSuccess: () => void;
}

export interface UseAssignAdminModalReturn {
  assignedAdmins: Admin[];
  loading: boolean;
  loadAssignedAdmins: () => Promise<void>;
  removeAdminFromRole: (adminId: string) => Promise<void>;
  renderRemoveButton: (record: Admin) => React.ReactNode;
}

// ==================== 管理员选择弹窗Hook ====================

/**
 * 管理员选择对话框 Hook
 */
export const useAdminSelectModal = ({
  roleId,
  visible,
  onSuccess,
}: UseAdminSelectModalConfig): UseAdminSelectModalReturn => {
  const [availableAdmins, setAvailableAdmins] = useState<Admin[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<Admin[]>([]);
  // 本次打开只加载一次的保护
  const didLoadOnOpenRef = useRef(false);
  // 记录最初已分配的管理员ID集合，用于比对增删
  const initialAssignedIdsRef = useRef<Set<string>>(new Set());

  // 使用通用表格选择 Hook
  const tableSelectModal = useTableSelectModal<Admin>({
    dataSource: availableAdmins,
    onSelectionChange: (selectedKeys, selectedRows) => {
      setSelectedAdmins(selectedRows);
    },
  });

  // 加载可用的管理员列表
  const loadAvailableAdmins = useCallback(async () => {
    try {
      tableSelectModal.setLoading(true);

      // 获取所有管理员
      const response = await roleApi.getAllAdmins();

      if (response.success && response.data) {
        setAvailableAdmins(response.data);
        // 预选中当前角色已分配的管理员
        if (roleId) {
          try {
            const assignedResp = await roleApi.getRoleAdmins(roleId);
            if (assignedResp.success && Array.isArray(assignedResp.data)) {
              const assigned = assignedResp.data as Admin[];
              const assignedIds = assigned.map((a) => a.id);
              initialAssignedIdsRef.current = new Set(assignedIds);
              tableSelectModal.setSelectedKeys(assignedIds);
              // 根据可选列表匹配出已选行，避免行数据为空
              const idSet = new Set(assignedIds);
              const preselectedRows = (response.data as Admin[]).filter((a: Admin) => idSet.has(a.id));
              tableSelectModal.setSelectedRows(preselectedRows);
              setSelectedAdmins(preselectedRows);
            }
          } catch (e) {
            // 忽略预选失败，保证基本可用
          }
        }
      } else {
        message.error(response.message || "获取管理员列表失败");
        setAvailableAdmins([]);
      }
    } catch (error) {
      console.error("获取管理员列表失败:", error);
      message.error("获取管理员列表失败");
      setAvailableAdmins([]);
    } finally {
      tableSelectModal.setLoading(false);
    }
  }, [tableSelectModal, roleId]);

  // 分配管理员到角色
  const assignAdminsToRole = useCallback(async (adminIds: string[]) => {
    if (!roleId || adminIds.length === 0) {
      return;
    }

    try {
      tableSelectModal.setLoading(true);

      const response = await roleApi.assignAdminsToRole(roleId, { adminIds });

      if (response.success) {
        message.success("管理员分配成功");
        if (onSuccess) {
          onSuccess(selectedAdmins);
        }
      } else {
        message.error(response.message || "管理员分配失败");
      }
    } catch (error) {
      console.error("管理员分配失败:", error);
      message.error("管理员分配失败");
    } finally {
      tableSelectModal.setLoading(false);
    }
  }, [roleId, selectedAdmins, onSuccess, tableSelectModal]);

  // 处理选择变化
  const handleSelectionChange = useCallback((selectedKeys: React.Key[], selectedRows: Admin[]) => {
    setSelectedAdmins(selectedRows);
    tableSelectModal.handleSelectionChange(selectedKeys, selectedRows);
  }, [tableSelectModal]);

  // 处理确认
  const handleConfirm = useCallback((onConfirm?: (selectedKeys: React.Key[], selectedRows: Admin[]) => void) => {
    if (selectedAdmins.length === 0) {
      message.warning("请选择要分配的管理员");
      return;
    }

    // 兼容：若为单选则仅取一个；若为多选则全部
    const selectedIds = new Set(selectedAdmins.map((a) => a.id));
    const initialIds = initialAssignedIdsRef.current;

    const toAdd: string[] = [];
    const toRemove: string[] = [];

    // 需要新增的：当前选中里有、初始没有
    selectedIds.forEach((id) => {
      if (!initialIds.has(id)) {
        toAdd.push(id);
      }
    });
    // 需要移除的：初始有、现在未选中
    initialIds.forEach((id) => {
      if (!selectedIds.has(id)) {
        toRemove.push(id);
      }
    });

    const doSubmit = async () => {
      try {
        tableSelectModal.setLoading(true);
        // 先新增
        if (toAdd.length > 0) {
          await assignAdminsToRole(toAdd);
        }
        // 再移除
        if (toRemove.length > 0) {
          await Promise.all(
            toRemove.map((adminId) => roleApi.removeAdminFromRole(roleId, adminId))
          );
          if (toRemove.length > 0) {
            message.success("移除管理员成功");
          }
        }
        // 成功后关闭并回调
        tableSelectModal.closeModal();
        if (onSuccess) {
          onSuccess(selectedAdmins);
        }
      } catch (e) {
        // 错误已在各自函数内提示，这里保证loading关闭
      } finally {
        tableSelectModal.setLoading(false);
      }
    };

    void doSubmit();
  }, [selectedAdmins, assignAdminsToRole, roleId, onSuccess, tableSelectModal]);

  // 处理取消
  const handleCancel = useCallback(() => {
    setSelectedAdmins([]);
    tableSelectModal.handleCancel();
  }, [tableSelectModal]);

  // 处理关闭
  const handleClose = useCallback(() => {
    setSelectedAdmins([]);
    tableSelectModal.handleClose();
  }, [tableSelectModal]);

  // 打开对话框
  const openModal = useCallback((initialConfig?: any) => {
    setSelectedAdmins([]);
    tableSelectModal.openModal({
      ...adminSelectModalConfig,
      ...initialConfig,
    });
  }, [tableSelectModal]);

  // 关闭对话框
  const closeModal = useCallback(() => {
    setSelectedAdmins([]);
    tableSelectModal.closeModal();
  }, [tableSelectModal]);

  // 当对话框真正可见时（由内部tableSelectModal控制）加载数据（每次打开只加载一次）
  useEffect(() => {
    if (tableSelectModal.visible && roleId) {
      if (!didLoadOnOpenRef.current) {
        didLoadOnOpenRef.current = true;
        loadAvailableAdmins();
      }
    } else {
      // 关闭时复位，便于下次打开重新加载
      didLoadOnOpenRef.current = false;
    }
  }, [tableSelectModal.visible, roleId, loadAvailableAdmins]);

  return {
    // 表格选择相关
    visible: tableSelectModal.visible,
    loading: tableSelectModal.loading,
    availableAdmins,
    selectedAdmins,
    selectedKeys: tableSelectModal.selectedKeys,
    config: tableSelectModal.config,

    // 方法
    openModal,
    closeModal,
    loadAvailableAdmins,
    handleSelectionChange,
    handleConfirm,
    handleCancel,
    handleClose,
    assignAdminsToRole,
  };
};

// ==================== 管理员分配弹窗Hook ====================

/**
 * 管理员分配弹窗Hook
 * 管理已分配管理员的显示和移除操作
 */
export const useAssignAdminModal = ({
  visible,
  roleId,
  onSuccess,
}: UseAssignAdminModalProps): UseAssignAdminModalReturn => {
  const [loading, setLoading] = useState(false);
  const [assignedAdmins, setAssignedAdmins] = useState<Admin[]>([]);
  const [loadingRef, setLoadingRef] = useState(false);

  // 加载已分配的管理员
  const loadAssignedAdmins = async () => {
    // 防止重复请求
    if (loadingRef) {
      // console.debug("请求正在进行中，跳过重复请求");
      return;
    }

    try {
      setLoading(true);
      setLoadingRef(true);
      // console.debug("开始加载角色管理员，roleId:", roleId);
      const response = await roleApi.getRoleAdmins(roleId);
      // console.debug("角色管理员API响应:", response);
      if (response.success && response.data) {
        setAssignedAdmins(response.data);
      } else {
        console.error("API响应格式错误:", response);
        message.error(response.message || "加载已分配管理员失败");
      }
    } catch (error) {
      console.error("加载已分配管理员失败:", error);
      message.error("加载已分配管理员失败");
    } finally {
      setLoading(false);
      setLoadingRef(false);
    }
  };

  // 移除管理员角色
  const removeAdminFromRole = async (adminId: string) => {
    try {
      setLoading(true);
      const response = await roleApi.removeAdminFromRole(roleId, adminId);
      if (response.success) {
        message.success("移除管理员成功");
        loadAssignedAdmins(); // 只刷新数据，不关闭模态框
      } else {
        message.error(response.message || "移除失败");
      }
    } catch (error) {
      message.error("移除管理员失败");
    } finally {
      setLoading(false);
    }
  };

  // 渲染移除按钮
  const renderRemoveButton = (record: Admin) => {
    if (!record || !record.id) {
      console.error("Invalid record in renderRemoveButton:", record);
      return React.createElement("span", { style: { color: "red" } }, "无效记录");
    }

    // admin用户不能移除
    if (record.adminAccount === "admin") {
      return React.createElement(
        "span",
        {
          style: { color: "#999", fontSize: "12px" },
        },
        "系统用户"
      );
    }

    return React.createElement(
      Popconfirm,
      {
        title: "确定要移除此管理员吗？",
        onConfirm: () => {
          // console.debug("移除管理员，ID:", record.id);
          removeAdminFromRole(record.id);
        },
        okText: "确定",
        cancelText: "取消",
      },
      React.createElement(
        Button,
        {
          type: "link",
          danger: true,
          size: "small",
          icon: React.createElement(DeleteOutlined),
        },
        "移除"
      )
    );
  };

  // 当模态框打开时加载数据
  useEffect(() => {
    if (visible && roleId) {
      loadAssignedAdmins();
    }
  }, [visible, roleId]);

  return {
    assignedAdmins,
    loading,
    loadAssignedAdmins,
    removeAdminFromRole,
    renderRemoveButton,
  };
};

// ==================== 管理员弹窗状态管理Hook ====================

/**
 * 管理员弹窗状态管理hook
 * @returns 管理员相关弹窗状态和操作方法
 */
export const useAdminModals = () => {
  const [assignAdminModalVisible, setAssignAdminModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  // 显示分配管理员弹窗
  const showAssignAdminModal = (record: any) => {
    setCurrentRecord(record);
    setAssignAdminModalVisible(true);
  };

  // 关闭分配管理员弹窗
  const hideAssignAdminModal = () => {
    setAssignAdminModalVisible(false);
    setCurrentRecord(null);
  };

  return {
    assignAdminModalVisible,
    currentRecord,
    showAssignAdminModal,
    hideAssignAdminModal,
    setCurrentRecord,
  };
};
